import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Canvas, useFrame, useLoader } from '@react-three/fiber';
import { TextureLoader } from 'three';
import { OrbitControls } from '@react-three/drei';
import { Mesh, Points } from 'three';
import FloatingButton from '../buttons/FloatingButton';

interface ParticleProps {
  isActive: boolean;
  isExploding: boolean;
}

function Particles({ isActive, isExploding }: ParticleProps) {
  const particlesRef = useRef<Points>(null);
  const velocitiesRef = useRef<Float32Array>();
  const particleCount = 200;
  
  const { positions, initialPositions } = useMemo(() => {
    const positions = new Float32Array(particleCount * 3);
    const initialPositions = new Float32Array(particleCount * 3);

    // Initialiser les positions des particules en forme de sphère
    for (let i = 0; i < particleCount; i++) {
      const radius = 3;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos((Math.random() * 2) - 1);
      
      const x = radius * Math.sin(phi) * Math.cos(theta);
      const y = radius * Math.sin(phi) * Math.sin(theta);
      const z = radius * Math.cos(phi);
      
      positions[i * 3] = x;
      positions[i * 3 + 1] = y;
      positions[i * 3 + 2] = z;
      
      initialPositions[i * 3] = x;
      initialPositions[i * 3 + 1] = y;
      initialPositions[i * 3 + 2] = z;
    }

    return { positions, initialPositions };
  }, [particleCount]);

  // Initialiser les vélocités pour l'explosion
  if (!velocitiesRef.current) {
    velocitiesRef.current = new Float32Array(particleCount * 3);
    for (let i = 0; i < particleCount; i++) {
      const i3 = i * 3;
      const x = positions[i3];
      const y = positions[i3 + 1];
      const z = positions[i3 + 2];
      const length = Math.sqrt(x * x + y * y + z * z);
      
      velocitiesRef.current[i3] = (x / length) * 0.2;
      velocitiesRef.current[i3 + 1] = (y / length) * 0.2;
      velocitiesRef.current[i3 + 2] = (z / length) * 0.2;
    }
  }

  useEffect(() => {
    if (!isActive && !isExploding) {
      // Réinitialiser les positions
      if (particlesRef.current) {
        const positions = particlesRef.current.geometry.attributes.position.array as Float32Array;
        for (let i = 0; i < particleCount * 3; i++) {
          positions[i] = initialPositions[i];
        }
        particlesRef.current.geometry.attributes.position.needsUpdate = true;
      }
    }
  }, [isActive, isExploding, initialPositions, particleCount]);

  useFrame(() => {
    if (!particlesRef.current) return;

    if (isActive && !isExploding) {
      // Animation normale (respiration)
      const time = Date.now() * 0.001;
      const positions = particlesRef.current.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < particleCount; i++) {
        const i3 = i * 3;
        const x = positions[i3];
        const y = positions[i3 + 1];
        const z = positions[i3 + 2];
        const distance = Math.sqrt(x * x + y * y + z * z);
        const factor = 1 + Math.sin(time + distance) * 0.1;
        
        positions[i3] = initialPositions[i3] * factor;
        positions[i3 + 1] = initialPositions[i3 + 1] * factor;
        positions[i3 + 2] = initialPositions[i3 + 2] * factor;
      }
      particlesRef.current.geometry.attributes.position.needsUpdate = true;
    } else if (isExploding && velocitiesRef.current) {
      // Animation d'explosion
      const positions = particlesRef.current.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < particleCount; i++) {
        const i3 = i * 3;
        positions[i3] += velocitiesRef.current[i3];
        positions[i3 + 1] += velocitiesRef.current[i3 + 1];
        positions[i3 + 2] += velocitiesRef.current[i3 + 2];
      }
      particlesRef.current.geometry.attributes.position.needsUpdate = true;
    }
  });

  return (
    <points ref={particlesRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={particleCount}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.05}
        color="#ffffff"
        transparent
        opacity={isActive || isExploding ? 0.8 : 0}
        sizeAttenuation
      />
    </points>
  );
}

interface SphereProps {
  onEnter: () => void;
  onHover: (isHovered: boolean) => void;
}

function Sphere({ onEnter, onHover }: SphereProps) {
  const meshRef = useRef<Mesh | null>(null);
  const texture = useLoader(TextureLoader, '/assets/images/logo.png');

  return (
    <mesh 
      ref={meshRef}
      onPointerOver={() => onHover(true)}
      onPointerOut={() => onHover(false)}
    >
      <sphereGeometry args={[2, 64, 64]} />
      <meshStandardMaterial 
        map={texture}
        metalness={0.1}
        roughness={0.6}
        color="#ffffff"
      />
    </mesh>
  );
}

interface Sphere3DProps {
  onEnter: () => void;
}

const Sphere3D: React.FC<Sphere3DProps> = ({ onEnter }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isExploding, setIsExploding] = useState(false);

  useEffect(() => {
    if (!isHovered && !isExploding) {
      setIsExploding(true);
      setTimeout(() => {
        setIsExploding(false);
      }, 1000); // Durée de l'explosion
    }
  }, [isHovered, isExploding]);

  return (
    <div style={{ 
      width: '100%', 
      height: '100vh',
      position: 'relative',
      zIndex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <div style={{ width: '100%', height: '80%' }}>
        <Canvas camera={{ position: [0, 0, 6] }}>
          <ambientLight intensity={1.2} />
          <pointLight position={[10, 10, 10]} intensity={2} />
          <pointLight position={[-10, -10, -10]} intensity={1} />
          <Sphere onEnter={onEnter} onHover={setIsHovered} />
          <Particles isActive={isHovered} isExploding={isExploding} />
          <OrbitControls 
            enableZoom={false}
            enablePan={false}
            enableRotate={true}
            autoRotate={false}
          />
        </Canvas>
      </div>
      <FloatingButton onClick={onEnter}>
        Découvrir
      </FloatingButton>
    </div>
  );
};

export default Sphere3D;
